<template>
  <!-- 合同趋势：签约数量/金额走势 + 合同类型分布 -->
  <div class="contract-trend">
    <list-page>
      <search-condition @search="resetSearch">
        <template #default>
          <el-date-picker v-model="dateRange" type="daterange" value-format="YYYY-MM-DD"
                          start-placeholder="签约开始" end-placeholder="签约结束" style="width: 260px"/>
          <el-select v-model="searchInfo.contractType" placeholder="全部合同类型" clearable>
            <template #prefix>合同类型</template>
            <el-option v-for="(label, value) in contractTypeMap" :key="value" :label="label" :value="Number(value)"/>
          </el-select>
          <employee-select v-model="searchInfo.salesId" placeholder="全部负责人">
            <template #prefix>负责人</template>
          </employee-select>
        </template>
        <template #option>
          <page-button type="read" el-type="primary" @click="resetSearch">查询</page-button>
        </template>
      </search-condition>

      <div v-loading="loading" class="stat-body">
        <div class="chart-card">
          <div class="section-title">合同数量 / 金额趋势</div>
          <div ref="barChart" class="chart-div"></div>
        </div>
        <div class="grid-2">
          <div class="chart-card">
            <div class="section-title">合同金额趋势</div>
            <div ref="lineChart" class="chart-div"></div>
          </div>
          <div class="chart-card">
            <div class="section-title">合同类型分布</div>
            <div ref="typeChart" class="hbar-div"></div>
          </div>
        </div>
      </div>
    </list-page>
  </div>
</template>

<script>
import * as echarts from "echarts";
import {markRaw} from "vue";
import ListPage from "@/components/h5/layout/ListPage.vue";
import ListTable from "@/components/h5/layout/ListTable.vue";
import SearchCondition from "@/components/h5/layout/SearchCondition.vue";
import PageButton from "@/components/h5/button/PageButton.vue";
import EmployeeSelect from "@/components/h5/data/EmployeeSelect.vue";
import contractApi from "@/api/dz-crm/partner/contractApi";
import {CONTRACT_TYPE_MAP} from "./contractDictionary";

export default {
  name: "ContractTrend",
  components: {EmployeeSelect, PageButton, SearchCondition, ListTable, ListPage},
  data() {
    return {
      loading: false,
      dateRange: [],
      searchInfo: {contractType: null, salesId: ""},
      stat: {monthList: [], contractCountList: [], amountList: [], typeStats: []},
      contractTypeMap: CONTRACT_TYPE_MAP,
      barChart: null,
      lineChart: null,
      typeChart: null,
    };
  },
  created() {
    this.loadStat();
  },
  unmounted() {
    [this.barChart, this.lineChart, this.typeChart].forEach(chart => chart && chart.dispose());
  },
  methods: {
    resetSearch() {
      this.loadStat();
    },
    async loadStat() {
      this.loading = true;
      try {
        const query = {
          contractType: this.searchInfo.contractType,
          salesId: this.searchInfo.salesId,
          startTime: this.dateRange && this.dateRange[0] ? this.dateRange[0] : null,
          endTime: this.dateRange && this.dateRange[1] ? this.dateRange[1] : null,
        };
        this.stat = await contractApi.statTrend(query) || {};
        this.$nextTick(() => this.renderCharts());
      } finally {
        this.loading = false;
      }
    },
    amountWan(value) {
      return Math.round((Number(value) || 0) / 100) / 100; // 元 → 万元，保留两位
    },
    renderCharts() {
      const months = this.stat.monthList || [];
      const counts = this.stat.contractCountList || [];
      const amounts = (this.stat.amountList || []).map(v => this.amountWan(v));
      this.barChart = this.renderBar(this.barChart, this.$refs.barChart, months, counts, amounts);
      this.lineChart = this.renderLine(this.lineChart, this.$refs.lineChart, months, amounts);
      this.typeChart = this.renderHBar(this.typeChart, this.$refs.typeChart, this.stat.typeStats || []);
    },
    renderBar(chart, dom, months, counts, amounts) {
      if (!dom) return chart;
      if (!chart) chart = markRaw(echarts.init(dom));
      chart.setOption({
        tooltip: {trigger: "axis", appendToBody: true, axisPointer: {type: "shadow"}},
        legend: {data: ["合同数量", "合同金额(万)"], top: 0, textStyle: {color: "#606266"}},
        grid: {left: "0", right: "0", bottom: "3%", top: "18%", containLabel: true},
        xAxis: {type: "category", data: months, axisLabel: {color: "#909399"}},
        yAxis: [
          {type: "value", name: "数量", minInterval: 1, splitLine: {lineStyle: {color: "#eee"}}, axisLabel: {color: "#909399"}},
          {type: "value", name: "万元", splitLine: {show: false}, axisLabel: {color: "#909399"}},
        ],
        series: [
          {name: "合同数量", type: "bar", barMaxWidth: 26, itemStyle: {color: "#1677ff", borderRadius: [3, 3, 0, 0]}, data: counts},
          {name: "合同金额(万)", type: "bar", yAxisIndex: 1, barMaxWidth: 26, itemStyle: {color: "#13b8b8", borderRadius: [3, 3, 0, 0]}, data: amounts},
        ],
      });
      return chart;
    },
    renderLine(chart, dom, months, amounts) {
      if (!dom) return chart;
      if (!chart) chart = markRaw(echarts.init(dom));
      chart.setOption({
        tooltip: {trigger: "axis", appendToBody: true, valueFormatter: value => `${value} 万元`},
        grid: {left: "0", right: "0", bottom: "3%", top: "10%", containLabel: true},
        xAxis: {type: "category", boundaryGap: false, data: months, axisLabel: {color: "#909399"}},
        yAxis: {type: "value", name: "万元", splitLine: {lineStyle: {color: "#eee"}}, axisLabel: {color: "#909399"}},
        series: [{type: "line", smooth: true, itemStyle: {color: "#1677ff"}, areaStyle: {opacity: 0.08}, data: amounts}],
      });
      return chart;
    },
    renderHBar(chart, dom, rows) {
      if (!dom) return chart;
      if (!chart) chart = markRaw(echarts.init(dom));
      const ordered = rows.slice().reverse();
      chart.setOption({
        tooltip: {trigger: "axis", appendToBody: true, axisPointer: {type: "shadow"}},
        grid: {left: "0", right: "40", bottom: "3%", top: "3%", containLabel: true},
        xAxis: {type: "value", minInterval: 1, splitLine: {lineStyle: {color: "#eee"}}, axisLabel: {color: "#909399"}},
        yAxis: {type: "category", data: ordered.map(row => row.name || "未设置"), axisTick: {show: false}, axisLabel: {color: "#909399"}},
        series: [{
          type: "bar",
          barWidth: "50%",
          itemStyle: {color: "#1677ff", borderRadius: [0, 3, 3, 0]},
          label: {show: true, position: "right", color: "#606266", fontSize: 11},
          data: ordered.map(row => row.count || 0),
        }],
      });
      return chart;
    },
  },
};
</script>

<style lang="scss" scoped>
.contract-trend {
  height: 100%;
  .stat-body {
    padding: 10px;
  }
  .grid-2 {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px;
  }
  .chart-card {
    border: 1px solid var(--yq-border-color, #ebeef5);
    border-radius: 8px;
    padding: 14px;
    margin-bottom: 12px;
    background: #fff;
  }
  .section-title {
    font-size: 15px;
    font-weight: 600;
    color: #1f2937;
    margin-bottom: 10px;
    padding-left: 8px;
    border-left: 3px solid var(--el-color-primary);
  }
  .chart-div {
    height: 300px;
  }
  .hbar-div {
    height: 300px;
  }
}
</style>
